import type {
  GroupedMissingCard,
  GroupedMissingCardOrder,
  MissingCardLine,
} from "@loot-os/fulfillment-core/order/packing-fold";
import { Button } from "@loot-os/ui/components/button";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { centsToDollars } from "../../lib/format";
import { type Dateish, toDate } from "../../lib/packing";

/**
 * The fold's dates are `Date`. tRPC sends them as ISO strings (this router has no transformer),
 * and `Dateish` is both, which is what the table and the poster actually read.
 */
export type MissingCardLineView = Omit<
  MissingCardLine,
  "createdAt" | "lastOrderedAt" | "procurementDatesLast4Weeks"
> & {
  createdAt: Dateish;
  lastOrderedAt: Dateish | null;
  procurementDatesLast4Weeks: Dateish[];
};

export type GroupedMissingCardView = Omit<
  GroupedMissingCard,
  "lastOrderedAt" | "procurementDatesLast4Weeks" | "earliestOrderAt" | "orders"
> & {
  lastOrderedAt: Dateish | null;
  procurementDatesLast4Weeks: Dateish[];
  earliestOrderAt: Dateish;
  orders: Array<Omit<GroupedMissingCardOrder, "createdAt"> & { createdAt: Dateish }>;
};

const lineColumns = createDataTableColumns<MissingCardLineView>();
const groupedColumns = createDataTableColumns<GroupedMissingCardView>();

const LINE_COLUMNS = lineColumns.columns([
  lineColumns.accessor("customerName", { header: "Customer name", size: 160 }),
  lineColumns.accessor("imageUrl", {
    header: "Image",
    size: 72,
    enableSorting: false,
    enableGlobalFilter: false,
    cell: ({ row }) =>
      row.original.imageUrl ? (
        <img src={row.original.imageUrl} alt="" className="mx-auto h-16 w-12 object-contain" />
      ) : null,
  }),
  lineColumns.accessor("priceCents", {
    header: "Price",
    size: 90,
    meta: { align: "end" },
    cell: ({ getValue }) => centsToDollars(getValue()),
  }),
  lineColumns.accessor("itemId", { header: "AssetGroupId", size: 140 }),
  lineColumns.accessor("sourceUrl", {
    header: "Link",
    size: 80,
    enableSorting: false,
    cell: ({ getValue }) => {
      const href = getValue();
      return href ? (
        <a className="underline" href={href} target="_blank" rel="noreferrer">
          TCGPlayer
        </a>
      ) : null;
    },
  }),
  lineColumns.accessor("printing", { header: "Printing", size: 110 }),
  lineColumns.accessor("orderId", {
    header: "Order ID",
    size: 120,
    cell: ({ getValue }) => (
      <Link
        className="font-mono text-xs underline-offset-2 hover:underline"
        to="/fulfillment/orders/$orderId"
        params={{ orderId: getValue() }}
      >
        {getValue().slice(0, 8)}
      </Link>
    ),
  }),
  lineColumns.accessor("amountProcuredLast4Weeks", {
    header: "Amount procured last 4 weeks",
    size: 120,
    meta: { align: "end" },
  }),
  lineColumns.accessor("lastOrderedAt", {
    header: "Last procured",
    size: 130,
    cell: ({ getValue }) => dateLabel(getValue()),
  }),
  lineColumns.accessor("createdAt", {
    header: "Created at",
    size: 170,
    cell: ({ getValue }) => toDate(getValue()).toLocaleString(),
  }),
  lineColumns.accessor("daysUntilOverSla", {
    header: "Days until over sla",
    size: 110,
    meta: { align: "end" },
  }),
  lineColumns.accessor("quantRemaining", {
    header: "Remaining Stock",
    size: 110,
    meta: { align: "end" },
  }),
  lineColumns.accessor("procurementDatesLast4Weeks", {
    header: "Procurement dates last 4 weeks",
    size: 180,
    enableSorting: false,
    cell: ({ getValue }) => dateList(getValue()),
  }),
  lineColumns.accessor("name", { header: "Card Name", size: 200 }),
  lineColumns.accessor("orderCostCents", {
    header: "Order Cost",
    size: 110,
    meta: { align: "end" },
    cell: ({ getValue }) => centsToDollars(getValue()),
  }),
  lineColumns.accessor("onHand", {
    header: "In Stock",
    size: 90,
    meta: { align: "end" },
  }),
]);

const GROUPED_COLUMNS = groupedColumns.columns([
  groupedColumns.accessor("imageUrl", {
    header: "Image",
    size: 72,
    enableSorting: false,
    enableGlobalFilter: false,
    cell: ({ row }) =>
      row.original.imageUrl ? (
        <img src={row.original.imageUrl} alt="" className="mx-auto h-16 w-12 object-contain" />
      ) : null,
  }),
  groupedColumns.accessor("priceCents", {
    header: "Price",
    size: 90,
    meta: { align: "end" },
    cell: ({ getValue }) => centsToDollars(getValue()),
  }),
  groupedColumns.accessor("itemId", { header: "AssetGroupId", size: 140 }),
  groupedColumns.accessor("procurementDatesLast4Weeks", {
    header: "Procurement dates",
    size: 180,
    enableSorting: false,
    cell: ({ getValue }) => dateList(getValue()),
  }),
  groupedColumns.accessor("sourceUrl", {
    header: "Link",
    size: 80,
    enableSorting: false,
    cell: ({ getValue }) => {
      const href = getValue();
      return href ? (
        <a className="underline" href={href} target="_blank" rel="noreferrer">
          TCGPlayer
        </a>
      ) : null;
    },
  }),
  groupedColumns.accessor("amountProcuredLast4Weeks", {
    header: "Amount procured",
    size: 120,
    meta: { align: "end" },
  }),
  groupedColumns.accessor("lastOrderedAt", {
    header: "Last procured",
    size: 130,
    cell: ({ getValue }) => dateLabel(getValue()),
  }),
  groupedColumns.accessor("earliestOrderAt", {
    header: "Earliest order date",
    size: 170,
    cell: ({ getValue }) => toDate(getValue()).toLocaleString(),
  }),
  groupedColumns.accessor("quantRemaining", {
    header: "Remaining Stock",
    size: 110,
    meta: { align: "end" },
  }),
  groupedColumns.accessor("name", { header: "Card Name", size: 200 }),
]);

/** Pink once the shipping deadline has passed, yellow inside five days, matching Retool. */
function slaColor(days: number): string | undefined {
  if (days <= 0) return "#ff91a4";
  if (days < 5) return "yellow";
  return undefined;
}

function dateLabel(value: Dateish | null): string {
  return value ? toDate(value).toLocaleDateString() : "";
}

function dateList(values: Dateish[]): string {
  return values.map((value) => toDate(value).toISOString().slice(0, 10)).join(", ");
}

/**
 * Retool's Missing Cards page: one row per short line, the same lines grouped by card, and the
 * bounty grid. The walk itself is `foldMissingCards`; this only presents it.
 */
export function MissingCards({
  lines,
  grouped,
  scanTruncated,
  isPending,
  showAll,
  includeLocked,
  from,
  to,
  onShowAll,
  onIncludeLocked,
  onFrom,
  onTo,
  onGenerate,
}: {
  lines: MissingCardLineView[];
  grouped: GroupedMissingCardView[];
  scanTruncated: boolean;
  isPending: boolean;
  showAll: boolean;
  includeLocked: boolean;
  from: string | undefined;
  to: string | undefined;
  onShowAll: (value: boolean) => void;
  onIncludeLocked: (value: boolean) => void;
  onFrom: (value: string | undefined) => void;
  onTo: (value: string | undefined) => void;
  onGenerate: () => void;
}) {
  const [bountyOpen, setBountyOpen] = useState(false);
  const [oldestFirst, setOldestFirst] = useState(false);

  const bounty = useMemo(() => {
    const rows = [...grouped];
    if (oldestFirst) {
      rows.sort(
        (a, b) => toDate(a.earliestOrderAt).getTime() - toDate(b.earliestOrderAt).getTime(),
      );
    } else {
      rows.sort((a, b) => a.quantRemaining - b.quantRemaining);
    }
    return rows;
  }, [grouped, oldestFirst]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <Button onClick={() => setBountyOpen(true)}>Open Missing List</Button>
        <label
          className="flex items-center gap-2 text-xs text-muted-foreground"
          htmlFor="missing-from"
        >
          <span className="w-20">Start Date</span>
          <Input
            id="missing-from"
            className="w-56"
            type="datetime-local"
            value={from ?? ""}
            onChange={(event) => onFrom(event.target.value || undefined)}
          />
        </label>
        <label
          className="flex items-center gap-2 text-xs text-muted-foreground"
          htmlFor="missing-to"
        >
          <span className="w-20">End Date</span>
          <Input
            id="missing-to"
            className="w-56"
            type="datetime-local"
            value={to ?? ""}
            onChange={(event) => onTo(event.target.value || undefined)}
          />
        </label>
      </div>

      {scanTruncated ? (
        <p className="text-sm text-muted-foreground">
          The list stops at 500 bags, oldest first. Newer bags past that are not included.
        </p>
      ) : null}

      {isPending ? <Skeleton className="h-64 w-full" /> : null}

      {isPending ? null : (
        <DataTable
          columns={LINE_COLUMNS}
          data={lines}
          getRowId={(row) => row.lineId}
          search="Search cards"
          csvName="missing-cards"
          emptyMessage="No rows found"
          maxHeight="max(20rem, calc(100dvh - 24rem))"
          rowStyle={(row) => {
            const backgroundColor = slaColor(row.daysUntilOverSla);
            return backgroundColor ? { backgroundColor } : undefined;
          }}
        />
      )}

      {isPending ? null : (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">Grouped by card</h2>
          <DataTable
            columns={GROUPED_COLUMNS}
            data={grouped}
            getRowId={(row) => row.itemId}
            search="Search cards"
            csvName="missing-cards-grouped"
            emptyMessage="No rows found"
            maxHeight="24rem"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Button onClick={onGenerate}>Generate Missing Card List</Button>
        <label className="flex items-center gap-2 text-sm" htmlFor="missing-all">
          <input
            id="missing-all"
            type="checkbox"
            className="accent-primary"
            checked={showAll}
            onChange={(event) => onShowAll(event.target.checked)}
          />
          Show All Missing Cards
        </label>
        <label className="flex items-center gap-2 text-sm" htmlFor="missing-locked">
          <input
            id="missing-locked"
            type="checkbox"
            className="accent-primary"
            checked={includeLocked}
            onChange={(event) => onIncludeLocked(event.target.checked)}
          />
          Show Locked Accounts
        </label>
      </div>

      <Dialog open={bountyOpen} onOpenChange={setBountyOpen}>
        <DialogContent className="sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Bounty Cards</DialogTitle>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm" htmlFor="missing-oldest">
            <input
              id="missing-oldest"
              type="checkbox"
              className="accent-primary"
              checked={oldestFirst}
              onChange={(event) => setOldestFirst(event.target.checked)}
            />
            Oldest First
          </label>
          <div className="grid max-h-[70vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-5">
            {bounty.map((card) => (
              <article key={card.itemId} className="flex flex-col gap-2 rounded-lg border p-2">
                <p className="text-sm">Quantity Needed: {-card.quantRemaining}</p>
                {card.imageUrl ? (
                  <img src={card.imageUrl} alt="" className="mx-auto h-40 w-full object-contain" />
                ) : null}
                <h3 className="truncate text-sm font-semibold" title={card.name}>
                  {card.name}
                </h3>
              </article>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
